import React, { useEffect, useState } from "react";
import {
  Box, Grid, Typography, Button,
  Paper,
  Table,
  TableCell,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupIcon from "@mui/icons-material/Group";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { OverAllLoader } from "../../components/OverAllLoader";
import api from "../../api/client";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import EditIcon from '@mui/icons-material/Edit';
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";
import { BrainCircuitIcon } from "lucide-react";

const ViewInterviewer = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const [overAllLoading, setOverAllLoading] = useState(false);
  const [interviewerData, setInterviewerData] = useState([]);
  const location = useLocation();
  const { InterviewerId } = location.state || "";
  const navigate = useNavigate();

  const handleNavigate = () => {
    navigate("/add-interviewer", {
      state: {
        InterviewerId: interviewerData?.user_id,
      },
    });
  };

  const fetchById = async () => {
    setOverAllLoading(true);
    try {
      const res = await api.post("ViewInterviewerByID", {
        user_id: InterviewerId,
      });
      setInterviewerData(res?.data?.data);
      setOverAllLoading(false);
    } catch (error) {
      console.error("Error fetching admin by ID:", error);
      setOverAllLoading(false);
    }
  };

  useEffect(() => {
    fetchById();
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {overAllLoading ? (
        <OverAllLoader message="Fetching interviewer details..." />
      ) : (
        <Box>
          {/* Back Button */}
          <Grid
            item
            sx={{
              display: "flex",
              alignItems: "center",
              color: "#1c97e3",
              gap: "4px",
              cursor: "pointer",
              width: "fit-content",
              mb: 2,
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                window.history.back();
              }
            }}
            onClick={() => window.history.back()}
          >
            <ArrowBackIcon sx={{ fontSize: "17px" }} />
            <Typography
              sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
            >
              Back
            </Typography>
          </Grid>

          {/* View Card */}
          <Box>
            {/* Header */}
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-between",
                borderBottom: "1px solid #d1d1d1",
              }}
            >
              <Grid
                sx={{
                  mb: 2,
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Grid
                  sx={{
                    backgroundColor: "#1c97e3",
                    p: 0.5,
                    borderRadius: "10px",
                  }}
                >
                  <GroupIcon sx={{ color: "white", fontSize: "35px" }} />
                </Grid>
                <Grid>
                  <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                    {getLabel('INTERVIEWER')} Details
                  </Typography>
                </Grid>
              </Grid>
              <Grid>
                <Button
                  variant="contained"
                  className="gradient-button"
                  startIcon={<EditIcon />}
                  onClick={handleNavigate}
                >
                  Edit
                </Button>
              </Grid>
            </Grid>

            {/* Interviewer Details */}
            <Box
              sx={{ p: 3, display: "flex", flexDirection: "column", rowGap: 3 }}
            >
              {/* Basic Info */}
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <GroupIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  Basic Information
                </Typography>
              </Grid>
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 2,
                  width: "100%",
                }}
              >
                <Grid
                  sx={{
                    display: "flex",
                    flex: "row",
                  }}
                >
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">First Name</Typography>
                    <Typography>{interviewerData?.first_name || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Last Name</Typography>
                    <Typography>{interviewerData?.last_name || "-"}</Typography>
                  </Grid>
                </Grid>
                <Grid
                  sx={{
                    display: "flex",
                    flex: "row",
                  }}
                >
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Gender</Typography>
                    <Typography>{interviewerData?.gender || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Date of Birth</Typography>
                    <Typography>{interviewerData?.dob || "-"}</Typography>
                  </Grid>
                </Grid>

                <Grid
                  sx={{
                    display: "flex",
                    flex: "row",
                  }}
                >
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Status</Typography>
                    <Typography>{interviewerData?.status || "-"}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Contact Info */}
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MailOutlineIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  Contact Information
                </Typography>
              </Grid>
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 2,
                }}
              >
                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Contact Number</Typography>
                    <Typography>{interviewerData?.phone || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Email</Typography>
                    <Typography>{interviewerData?.email || "-"}</Typography>
                  </Grid>
                </Grid>

                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Address</Typography>
                    <Typography>{interviewerData?.address || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">City</Typography>
                    <Typography>{interviewerData?.city || "-"}</Typography>
                  </Grid>
                </Grid>

                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">State</Typography>
                    <Typography>{interviewerData?.state || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600">Pincode</Typography>
                    <Typography>{interviewerData?.pincode || "-"}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Address Info */}
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 2,
                }}
              >
              </Grid>

              {/* Institute Info */}
              <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BusinessIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  {getLabel('INSTITUTE')} Information
                </Typography>
              </Grid>
              <Grid
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 2,
                }}
              >
                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600"> {getLabel('INSTITUTE')}  Name</Typography>
                    <Typography>{interviewerData?.institute_name || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600"> {getLabel('BATCHES')}  Name</Typography>
                    <Typography>{interviewerData?.batch_name || "-"}</Typography>
                  </Grid>
                </Grid>

                <Grid sx={{ display: "flex" }}>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600"> Designation</Typography>
                    <Typography>{interviewerData?.interviewer_profile?.designation || "-"}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ width: "50%" }}>
                    <Typography fontWeight="600"> Department</Typography>
                    <Typography>{interviewerData?.interviewer_profile?.department || "-"}</Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* Skills Info */}
              <Grid sx={{ display: "flex", gap: 1 }}>
                <BrainCircuitIcon
                  style={{
                    color: "#1c97e3",
                    height: "27px",
                    width: "27px",
                    fontWeight: "bold",
                  }}
                />
                <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                  {`${getLabel('SUBJECT')} Information`}
                </Typography>
              </Grid>

              <Grid sx={{ display: "flex", gap: 3 }}>
                <TableContainer
                  component={Paper}
                >
                  <Table>
                    <TableHead>
                      <TableRow className="tableHeader">
                        <TableCell sx={{ py: 1, width: '250px' }} className="tableHeader">
                          <b>Skill</b>
                        </TableCell>

                        <TableCell
                          className="tableHeader"
                          sx={{ width: "250px", py: 1 }}
                        >
                          <b>Proficiency</b>
                        </TableCell>

                        <TableCell
                          className="tableHeader"
                          sx={{ width: "250px", py: 1 }}
                        >
                          <b>Relevant Experience</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody className="tablebody">
                      {interviewerData?.interviewer_profile?.skills.map((skill, index) => {
                        return (
                          <TableRow
                            key={skill.skill_id}
                            sx={{
                              "& .MuiTableCell-root": {
                                borderBottom: "1px solid #eeeeee",
                                fontSize: "13px",
                              },
                              backgroundColor:
                                index % 2 === 1
                                  ? "#FAFAFA"
                                  : "inherit",
                            }}
                          >
                            {/* Skill */}
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 500,
                                }}
                              >
                                {skill.name}
                              </Typography>
                            </TableCell>

                            {/* Proficiency */}
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 500,
                                }}
                              >
                                {skill.proficiency}
                              </Typography>
                            </TableCell>

                            {/* Relevant Experience */}
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 500,
                                }}
                              >
                                {skill.years_of_experience}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ViewInterviewer;
